import { useEffect, useState } from 'react';
import useApi from 'vl-common/src/hooks/useApi';
import { useQuery } from '@tanstack/react-query';

export interface TimelineTaskQueryParamProps {
  sort?: string;
  order?: string;
  task_type?: string;
  task_status_code?: string[];
  assigned_by?: string;
  assigned_to?: string;
  company_id?: string;
  active?: boolean | null; // Tab Filter
  note_search?: string; // Search
  spec_code?: string; // Filter
  restrict_mode?: string;
  page?: number;
}

interface UseTimelineTasksProps {
  case_id: string | undefined;
  taskQueryParams: TimelineTaskQueryParamProps;
}

const useTimelineTasks = ({ case_id, taskQueryParams }: UseTimelineTasksProps) => {
  const { Api } = useApi();
  const [currentTaskPage, setCurrentTaskPage] = useState(1);
  const [isNextPage, setIsNextPage] = useState(false);
  const [isPreviousPage, setIsPreviousPage] = useState(false);
  const {
    data: taskData,
    refetch: refetchTasks,
    isLoading: isTasksLoading
  } = useQuery({
    queryKey: ['taskList', case_id, taskQueryParams, currentTaskPage],
    queryFn: () => {
      const response = Api.task.getTaskList({
        ...taskQueryParams,
        case_id: case_id,
        page: currentTaskPage
      });
      return response;
    },
    enabled: !!case_id
  });

  const onGetNextPage = () => {
    if (isNextPage) {
      setCurrentTaskPage((prev) => prev + 1);
    }
  };

  const onGetPreviousPage = () => {
    if (isPreviousPage) {
      setCurrentTaskPage((prev) => prev - 1);
    }
  };

  const onGetFirstPage = () => {
    setCurrentTaskPage(1);
  };

  useEffect(() => {
    if (taskData && taskData?.data?.Tasks?.length > 0) {
      setIsNextPage(taskData.data.Tasks.length === 10);
      setIsPreviousPage(currentTaskPage > 1);
    } else {
      setIsNextPage(false);
      setIsPreviousPage(currentTaskPage > 1);
    }
  }, [taskData, currentTaskPage, isTasksLoading]);

  return {
    taskData,
    refetchTasks,
    currentTaskPage,
    onGetNextPage,
    onGetPreviousPage,
    onGetFirstPage,
    isNextPage,
    isPreviousPage,
    isTasksLoading
  };
};

export default useTimelineTasks;
